import tokens from '@kluein/design-tokens';
import { NectarIcon } from '@kluein/nectar';
import { useCallback } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { useNavigate } from 'react-router-dom';

type TransitionType = 'askklue-to-dashboard' | 'none';

export type SourceElementData = {
  top: number;
  left: number;
  width: number;
  height: number;
  html: string;
};

const ANIMATION_CONFIG = {
  SPEED_PX_PER_SECOND: 500,
  MIN_DURATION_MS: 300,
  MAX_DURATION_MS: 1000,
  OVERLAY_FADE_MS: 300,
  RENDER_WAIT_MS: 800,
};

export const useTransitionNavigation = () => {
  const navigate = useNavigate();

  const startAskKlueToDashboardAnimation = useCallback(
    (to: string) => {
      let inputClone: HTMLElement | null = null;
      let boardsClone: HTMLElement | null = null;
      let logoRoot: Root | null = null;

      const cleanup = () => {
        if (inputClone?.parentNode) {
          document.body.removeChild(inputClone);
        }
        if (boardsClone?.parentNode) {
          document.body.removeChild(boardsClone);
        }
      };

      try {
        // Safely get all required DOM elements
        const sourceElement = document.getElementById(
          'ask-klue-input-container',
        );
        const boardsElement = document.getElementById(
          'ask-klue-page-dashboard-view',
        );
        const navBarSearch = document.getElementById(
          'ask-klue-unified-search-box',
        );
        const askKluePageContent = document.getElementById(
          'ask-klue-page-ask-klue-content',
        );

        // Fallback to simple navigation if any required elements are missing
        if (
          !sourceElement ||
          !navBarSearch ||
          !boardsElement ||
          !askKluePageContent
        ) {
          navigate(to);
          return;
        }

        // Prepare source element data and transition state for navigation
        const sourceRect = sourceElement.getBoundingClientRect();
        const targetRect = navBarSearch.getBoundingClientRect();

        const sourceData: SourceElementData = {
          top: sourceRect.top,
          left: sourceRect.left,
          width: sourceRect.width,
          height: sourceRect.height,
          html: sourceElement.outerHTML,
        };
        // Calculate distance using Manhattan distance for performance
        const distance =
          Math.abs(targetRect.left - sourceRect.left) +
          Math.abs(targetRect.top - sourceRect.top);

        // Calculate animation duration based on distance
        const duration = Math.max(
          ANIMATION_CONFIG.MIN_DURATION_MS,
          Math.min(
            ANIMATION_CONFIG.MAX_DURATION_MS,
            (distance / ANIMATION_CONFIG.SPEED_PX_PER_SECOND) * 1000,
          ),
        );

        // Create animated clone of the input (empty box)
        inputClone = sourceElement.cloneNode(false) as HTMLElement; // shallow clone - no children
        inputClone.style.position = 'fixed';
        inputClone.style.top = `${sourceRect.top}px`;
        inputClone.style.left = `${sourceRect.left}px`;
        inputClone.style.width = `${sourceRect.width}px`;
        inputClone.style.height = `${sourceRect.height}px`;
        inputClone.style.zIndex = '10001';
        inputClone.style.transition = `all ${duration}ms ease-out`;
        inputClone.style.background =
          'linear-gradient(to right, rgb(248, 248, 255) 115px, white 115px)';

        const logoContainer = document.createElement('div');
        logoContainer.style.transform =
          'translateY(50%) translateY(-6px) translateX(12px) scale(2)';
        logoRoot = createRoot(logoContainer);
        logoRoot.render(
          <NectarIcon.KlueLogo color={tokens.color.primitives.violet[500]} />,
        );
        inputClone.appendChild(logoContainer);

        document.body.appendChild(inputClone);

        // Hide the original input
        sourceElement.style.opacity = '0';
        askKluePageContent.style.opacity = '0';

        // Calculate actual navbar height (including any banners above it)
        const navbarHeight = targetRect.bottom;
        boardsElement.style.setProperty('--navbar-height', `${navbarHeight}px`);

        void inputClone.offsetHeight; // layout flush (force reflow)
        void askKluePageContent.offsetHeight; // layout flush (force reflow)
        inputClone.style.top = `${targetRect.top}px`;
        inputClone.style.left = `${targetRect.left}px`;
        inputClone.style.width = `${targetRect.width}px`;
        inputClone.style.height = `${targetRect.height}px`;

        boardsElement.style.transition = `all ${duration}ms ease-out`;
        boardsElement.classList.add('full');

        setTimeout(() => {
          try {
            // Clone the boards element to use as overlay
            boardsClone = boardsElement.cloneNode(true) as HTMLElement;
            const boardsRect = boardsElement.getBoundingClientRect();
            boardsClone.style.position = 'fixed';
            boardsClone.style.top = `${boardsRect.top}px`;
            boardsClone.style.left = `${boardsRect.left}px`;
            boardsClone.style.width = `${boardsRect.width}px`;
            boardsClone.style.height = `${boardsRect.height}px`;
            boardsClone.style.zIndex = '10000';
            boardsClone.style.pointerEvents = 'none';
            boardsClone.style.overflow = 'hidden';
            document.body.appendChild(boardsClone);

            navigate(to, {
              state: {
                transition: 'askklue-to-dashboard',
                sourceElementData: sourceData,
              },
            });

            // Wait for dashboard to render, then fade out overlays
            setTimeout(() => {
              try {
                if (boardsClone) {
                  boardsClone.style.transition = `opacity ${ANIMATION_CONFIG.OVERLAY_FADE_MS}ms ease-out`;
                  boardsClone.style.opacity = '0';
                }
                if (inputClone) {
                  inputClone.style.transition = `opacity ${ANIMATION_CONFIG.OVERLAY_FADE_MS}ms ease-out`;
                  inputClone.style.opacity = '0';
                }

                setTimeout(() => {
                  cleanup();
                }, ANIMATION_CONFIG.OVERLAY_FADE_MS);
              } catch {
                cleanup();
              }
            }, ANIMATION_CONFIG.RENDER_WAIT_MS);
          } catch {
            cleanup();
            navigate(to);
          }
        }, duration);
      } catch {
        cleanup();
        navigate(to);
      }
    },
    [navigate],
  );

  const navigateWithTransition = useCallback(
    (to: string, transitionType: TransitionType) => {
      if (transitionType === 'askklue-to-dashboard') {
        startAskKlueToDashboardAnimation(to);
      } else {
        navigate(to);
      }
    },
    [navigate, startAskKlueToDashboardAnimation],
  );

  return {
    navigateWithTransition,
  };
};
